import type { EntryDto, Puzzle, Rank } from '../api/types';
import { puzzleLabel } from './puzzleLabel';

const SITE_URL = 'https://past.bennybuilds.de';

/**
 * Erzeugt den WhatsApp-kompatiblen Share-Text.
 *
 * Aufbau:
 *   Header (📅 Pastpuzzle #N – Datum)
 *   ⏳ Fehlt noch: … (nur, wenn vorab geteilt wird und Spieler fehlen)
 *   Rangliste (🥇/🥈/🥉/✗) inkl. Tipp-Zeitpunkt
 *   ---
 *   Pro Spieler: Name + Versuche im Original-Format pro Zeile,
 *   ergänzt um die getippte Jahreszahl (wenn Lösung bekannt).
 *   ---
 *   Lösung (wenn bekannt)
 *   Deep-Link auf das Rätsel des Tages.
 *
 * `missingPlayers`: Namen der Spieler, die noch nicht getippt haben.
 * Nicht-leer nur beim Vorab-Teilen (Zwischenstand) — dann wird oben ein
 * Hinweis eingeblendet.
 */
export function formatShareText(
  puzzle: Puzzle,
  entries: EntryDto[],
  missingPlayers: string[] = [],
): string {
  const ranked = [...entries].sort(byRank);

  const dateIso = puzzle.puzzle_date.slice(0, 10);
  const header = `📅 Pastpuzzle ${puzzleLabel(puzzle)} – ${formatDate(puzzle.puzzle_date)}`;
  const rankingLines = ranked.map((e) => formatRankingLine(e));

  const blocks: string[] = [];
  for (const e of ranked) {
    const lines = formatAttemptLines(e, puzzle.solution_year);
    blocks.push([`${e.player_name}:`, ...lines].join('\n'));
  }

  const out: string[] = [header];
  if (missingPlayers.length > 0) {
    out.push(`⏳ Fehlt noch: ${missingPlayers.join(', ')}`);
  }
  if (puzzle.also_played_dates && puzzle.also_played_dates.length > 0) {
    const formatted = puzzle.also_played_dates.map(formatDate).join(', ');
    out.push(`🔁 Schon mal gespielt: ${formatted}`);
  }
  out.push(...rankingLines, '');
  out.push(blocks.join('\n\n'));
  if (puzzle.solution_year != null) {
    out.push('', `🔎 Lösung: ${puzzle.solution_year}`);
  }
  out.push('', `${SITE_URL}/?date=${dateIso}`);
  return out.join('\n');
}

/**
 * Erzeugt den Share-Text für das eigene Ergebnis im Originalformat von
 * pastpuzzle.de — also genau das, was die Pastpuzzle-App nach dem
 * Spielen kopiert. Gedacht zum Teilen mit Leuten außerhalb unserer
 * Gruppe (für die intern-formatierte Team-Variante siehe
 * `formatShareText`).
 *
 *   #pastpuzzle 477
 *   🟩🟩🟥🟥 (+4)
 *   🟩🟥🟩🟥 (-109)
 *   🟩🟥🟥🟨 (+152)
 *   🟩🟩🟩🟥 (-1)
 *
 *   x/4 🟥
 *   https://www.pastpuzzle.de
 */
export function formatOwnShareText(puzzle: Puzzle, entry: EntryDto): string {
  const header = `#pastpuzzle ${puzzleLabel(puzzle)}`;
  const attemptLines = formatRawAttemptLines(entry);
  const resultLine = formatResultLine(entry);
  return [header, ...attemptLines, '', resultLine, 'https://www.pastpuzzle.de'].join('\n');
}

function formatRawAttemptLines(entry: EntryDto): string[] {
  const slots: ReadonlyArray<readonly [string | null, number | null]> = [
    [entry.attempt_1_emojis, entry.attempt_1_diff],
    [entry.attempt_2_emojis, entry.attempt_2_diff],
    [entry.attempt_3_emojis, entry.attempt_3_diff],
    [entry.attempt_4_emojis, entry.attempt_4_diff],
  ];
  const lines: string[] = [];
  const limit = Math.min(entry.attempts, 4);
  for (let i = 0; i < limit; i++) {
    const slot = slots[i];
    if (!slot) continue;
    const [emojis, diff] = slot;
    if (emojis == null || diff == null) continue;
    lines.push(`${emojis} ${formatDiff(diff)}`);
  }
  return lines;
}

function formatResultLine(entry: EntryDto): string {
  if (!entry.solved) return 'x/4 🟥';
  // Pastpuzzle.de hängt bei 4/4 ein 🟩 statt einer Medaille an.
  const medal = entry.attempts === 1
    ? '🥇'
    : entry.attempts === 2
      ? '🥈'
      : entry.attempts === 3
        ? '🥉'
        : '🟩';
  return `${entry.attempts}/4 ${medal}`;
}

function formatRankingLine(entry: EntryDto): string {
  const rank = entry.rank;
  const result = entry.solved ? `(${entry.attempts} Versuche)` : '(DNF)';
  const time = formatSubmittedAt(entry.submitted_at);
  const suffix = time ? `${result} · ${time}` : result;
  if (rank === 'DNF' || rank == null) {
    return `✗ ${entry.player_name} ${suffix}`;
  }
  if (rank === 1) return `🥇 ${entry.player_name} ${suffix}`;
  if (rank === 2) return `🥈 ${entry.player_name} ${suffix}`;
  if (rank === 3) return `🥉 ${entry.player_name} ${suffix}`;
  return `${rank}. ${entry.player_name} ${suffix}`;
}

/**
 * Tipp-Zeitpunkt als „TT.MM. HH:MM" (z.B. „08.06. 09:14"). Leer-String
 * bei ungültigem Input — die Rangliste fällt dann sauber auf nur den
 * Versuchs-Teil zurück.
 */
function formatSubmittedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

/**
 * Pro Versuch eine Zeile im Pastpuzzle-Original-Format
 * `🟩🟩🟥🟥 (+4)`, optional ergänzt um die getippte Jahreszahl
 * (Lösung + Differenz). Bei DNF wird die letzte Zeile auf
 * `x/4 🟥` gesetzt.
 */
function formatAttemptLines(entry: EntryDto, solution: number | null): string[] {
  const slots: ReadonlyArray<readonly [string | null, number | null]> = [
    [entry.attempt_1_emojis, entry.attempt_1_diff],
    [entry.attempt_2_emojis, entry.attempt_2_diff],
    [entry.attempt_3_emojis, entry.attempt_3_diff],
    [entry.attempt_4_emojis, entry.attempt_4_diff],
  ];
  const lines: string[] = [];
  const limit = Math.min(entry.attempts, 4);
  for (let i = 0; i < limit; i++) {
    const slot = slots[i];
    if (!slot) continue;
    const [emojis, diff] = slot;
    if (emojis == null || diff == null) continue;
    const yearPart = solution != null ? ` ${solution + diff}` : '';
    lines.push(`${emojis} ${formatDiff(diff)}${yearPart}`);
  }
  if (!entry.solved) {
    lines.push('x/4 🟥');
  }
  return lines;
}

function formatDiff(diff: number): string {
  if (diff === 0) return '(0)';
  return diff > 0 ? `(+${diff})` : `(${diff})`;
}

function byRank(a: EntryDto, b: EntryDto): number {
  return rankSort(a.rank) - rankSort(b.rank);
}

function rankSort(rank: Rank | null): number {
  if (rank == null || rank === 'DNF') return Number.MAX_SAFE_INTEGER;
  return rank;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
